"use client";

import type React from "react";
import { renderSeverityIcon, type ToastSeverity } from "@/fetch-ui/FetchToast";
import { cn } from "@/lib/utils/index";
import * as Toast from "@radix-ui/react-toast";
import { X } from "lucide-react";
import Link from "next/link";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

/** Action slot — either a navigation link or an inline callback (e.g., Undo). */
export type ToastAction = { label: string; href: string } | { label: string; onClick: () => void };

/** Options for toast description, action button, and duration. */
export interface ToastOptions {
  description?: string;
  action?: ToastAction;
  /** Auto-dismiss duration in milliseconds. Defaults to 7000. */
  duration?: number;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastSeverity, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

/**
 * Returns the toast context for showing toast notifications.
 *
 * @returns The toast context exposing `showToast`.
 * @throws If called outside of a {@link FetchToastProvider}.
 */
export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a FetchToastProvider");
  }
  return context;
};

interface FetchToastProviderProps {
  children: React.ReactNode;
}

/**
 * Provides toast context and renders active toast notifications.
 *
 * @param props - Component props.
 * @param props.children - The subtree that can trigger toasts via `useToast`.
 * @returns The provider wrapping `children` with the toast viewport.
 */
export const FetchToastProvider = ({ children }: FetchToastProviderProps) => {
  // States
  const [toasts, setToasts] = useState<
    Array<{
      id: string;
      message: string;
      type: ToastSeverity;
      open: boolean;
      description?: string;
      action?: ToastAction;
    }>
  >([]);

  // Portal the toasts to document.body (below) only after mount — document is unavailable during SSR.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Handlers
  // Stable across renders (only `setToasts` in scope, itself a stable setter) — callers throughout
  // the app put `showToast` in effect/callback dependency arrays, so a fresh closure per render here
  // would cascade into those effects re-running on every toast shown anywhere.
  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.map((toast) => (toast.id === id ? { ...toast, open: false } : toast)));
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 300);
  }, []);

  const showToast = useCallback(
    (message: string, type: ToastSeverity = "success", options?: ToastOptions) => {
      const id = Math.random().toString(36).substring(2, 11);
      const { duration = 7000, ...rest } = options ?? {};
      setToasts((prev) => [...prev, { id, message, type, open: true, ...rest }]);

      setTimeout(() => {
        removeToast(id);
      }, duration);
    },
    [removeToast]
  );

  const contextValue = useMemo(() => ({ showToast }), [showToast]);

  // Render
  return (
    <ToastContext.Provider value={contextValue}>
      <Toast.Provider swipeDirection="down">
        {children}
        {/* Portal the toasts to <body> so their z-index isn't trapped inside an ancestor stacking
            context and pushed under body-level portals like FetchModal (which renders on body).
            `data-react-aria-top-layer` tells an open React Aria modal (the nowadays-ui kit) to
            leave this layer alone: not inert, not hidden from assistive tech, and a click on a
            toast does not count as a click outside the modal. */}
        {mounted &&
          createPortal(
            <div data-react-aria-top-layer="true">
              {toasts.map((toast) => (
                <Toast.Root
                  key={toast.id}
                  open={toast.open}
                  className={cn(
                    "pointer-events-auto flex w-full items-center justify-between gap-4 overflow-hidden rounded-2xl px-4 py-3.5 shadow-lg transition-[transform,opacity]",
                    "data-[swipe=cancel]:translate-y-0 data-[swipe=end]:translate-y-(--radix-toast-swipe-end-y) data-[swipe=move]:translate-y-(--radix-toast-swipe-move-y) data-[swipe=move]:transition-none",
                    "data-[state=open]:animate-slideUpAndFade data-[state=closed]:animate-slideDownAndFadeOut data-[swipe=end]:animate-slideDownAndFadeOut motion-reduce:animate-none",
                    "bg-gray-900 text-white"
                  )}
                  onOpenChange={(open) => {
                    if (!open) removeToast(toast.id);
                  }}
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    {!toast.action && renderSeverityIcon(toast.type)}
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <Toast.Title className="text-sm font-medium leading-snug">{toast.message}</Toast.Title>
                      {toast.description && (
                        <Toast.Description className="text-sm leading-snug text-white/70">
                          {toast.description}
                        </Toast.Description>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    {toast.action &&
                      (() => {
                        const action = toast.action;
                        const pillClassName =
                          "shrink-0 rounded-full bg-white px-4 py-1.5 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60";
                        return (
                          <Toast.Action altText={action.label} asChild>
                            {"href" in action ? (
                              <Link href={action.href} className={pillClassName}>
                                {action.label}
                              </Link>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  action.onClick();
                                  removeToast(toast.id);
                                }}
                                className={pillClassName}
                              >
                                {action.label}
                              </button>
                            )}
                          </Toast.Action>
                        );
                      })()}
                    <Toast.Close
                      className="-mr-1 shrink-0 rounded-lg p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                      aria-label="Dismiss notification"
                    >
                      <X className="h-4 w-4" />
                    </Toast.Close>
                  </div>
                </Toast.Root>
              ))}
              {/* `--toast-bottom-offset` lifts the stack above a floating bar while one is on screen
                  (the admin selection bar sets it on the root). Unset, it falls back to 0. */}
              <Toast.Viewport className="fixed inset-x-0 bottom-(--toast-bottom-offset,0px) z-100001 mx-auto flex max-h-screen w-full flex-col gap-2 p-4 sm:max-w-[420px]" />
            </div>,
            document.body
          )}
      </Toast.Provider>
    </ToastContext.Provider>
  );
};
